import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { LoadingSkeleton, ProgressButton } from 'components';
import AccountCurrency from 'components/AccountCurrency';
import { useCallback, useEffect, useState } from 'react';
import { useEditUserMutation, useGetUserQuery, useLazyGetStripeKeysQuery } from 'store/api/version-api.slice';

import LinkAccountsButton from './LinkAccountsButton';
import { styles } from './styles';

const LinkAccountsSelect = ({ handleNext }) => {
	const [showSquare, setShowSquare] = useState(false);
	const [showStripe, setShowStripe] = useState(false);
	const [paymentProcessor, setPaymentProcessor] = useState('none');
	const [stPubKey, setStPubKey] = useState('');
	const [stSecKey, setStSecKey] = useState('');

	const { data: user = {} } = useGetUserQuery();
	const [editUser, { isLoading: isEditLoading }] = useEditUserMutation();
	const [getStripeKeys, { isLoading: areStripeKeysLoading }] = useLazyGetStripeKeysQuery();

	const userPaymentCurrency = user.payment_currency;

	const stPubKeyValue = user.st_pub_key;
	const stSecKeyValue = user.st_sec_key;

	useEffect(() => {
		setPaymentProcessor(paymentProcessor);
		if (paymentProcessor === 'square') {
			setShowSquare(true);
			setShowStripe(false);
		}
		if (paymentProcessor === 'stripe') {
			setShowSquare(false);
			setShowStripe(true);
		}
		if (paymentProcessor === 'none' || typeof paymentProcessor === 'undefined') {
			setShowSquare(false);
			setShowStripe(false);
		}
	}, [paymentProcessor]);

	useEffect(() => {
		if (stPubKeyValue) {
			setStPubKey(stPubKeyValue);
		}
		if (stSecKeyValue) {
			setStSecKey(stSecKeyValue);
		}
	}, [stPubKeyValue, stSecKeyValue]);

	const handleSelect = e => {
		setPaymentProcessor(e.target.value);
	};

	const handleTextChange = e => {
		if (e.target.name === 'st_pub') {
			setStPubKey(e.target.value);
		}
		if (e.target.name === 'st_sec') {
			setStSecKey(e.target.value);
		}
	};

	const updateUser = () => {
		if (paymentProcessor === 'stripe') {
			editUser({
				payment_currency: userPaymentCurrency,
				payment_processor: paymentProcessor,
				st_pub_key: stPubKey,
				st_sec_key: stSecKey
			});
		}

		handleNext?.();
	};

	const receiveStripeKeys = useCallback(async () => {
		const response = await getStripeKeys().unwrap();

		setStPubKey(response.st_pub_key || '');
		// ? Ask Tony is it secure to receive secret key on the frontend side
		setStSecKey(response.st_sec_key || '');
	}, [getStripeKeys]);

	useEffect(() => {
		if (showStripe) {
			receiveStripeKeys();
		}
	}, [showStripe, receiveStripeKeys]);

	useEffect(() => {
		if (user.payment_processor) {
			setPaymentProcessor(user.payment_processor);
		}
	}, [user.payment_processor]);

	return (
		<>
			<AccountCurrency paymentProcessor={paymentProcessor} />
			<ListItem>
				<Typography sx={{ width: '100%' }} variant="h5" color="primary">
					{!user.id ? <Skeleton variant="rounded" /> : 'PAYMENT PROCESSOR'}
				</Typography>
			</ListItem>
			<ListItem>
				<LoadingSkeleton isLoading={!user.id}>
					<Select fullWidth onChange={handleSelect} value={paymentProcessor} defaultValue={paymentProcessor}>
						<MenuItem value="none">None</MenuItem>
						<MenuItem value="square">Square</MenuItem>
						<MenuItem value="stripe">Stripe</MenuItem>
					</Select>
				</LoadingSkeleton>
			</ListItem>
			<ListItem>
				{showSquare && (
					<LinkAccountsButton id="square-link-button" account={'square'} handleNext={handleNext} />
				)}
			</ListItem>
			{showStripe && (
				<>
					<ListItem>
						<LoadingSkeleton isLoading={areStripeKeysLoading}>
							<TextField
								onChange={handleTextChange}
								value={stPubKey}
								autoComplete="st_pub"
								name="st_pub"
								variant="filled"
								required
								fullWidth
								id="stPubKey"
								label="Stripe Public Key"
								helperText="Ex: pk_xxxx_xxxx"
							/>
						</LoadingSkeleton>
					</ListItem>
					<ListItem>
						<LoadingSkeleton isLoading={areStripeKeysLoading}>
							<TextField
								onChange={handleTextChange}
								value={stSecKey}
								autoComplete="st_sec"
								name="st_sec"
								variant="filled"
								required
								fullWidth
								id="stSecKey"
								label="Stripe Secret Key"
								helperText="Ex: sk_xxxx_xxxx"
							/>
						</LoadingSkeleton>
					</ListItem>
					<ListItem>
						<LoadingSkeleton isLoading={areStripeKeysLoading}>
							<ProgressButton
								circleColor="secondary"
								isLoading={isEditLoading}
								button={
									<Button
										fullWidth
										variant="contained"
										color="primary"
										sx={styles.submit}
										onClick={updateUser}
									>
										SAVE
									</Button>
								}
							/>
						</LoadingSkeleton>
					</ListItem>
				</>
			)}
		</>
	);
};

export default LinkAccountsSelect;
